import { useRouter } from 'next/navigation';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProfileSectionEnum } from '@/components/PersonalAccount/PersonalAccount.types';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import {
  ApplicationsIcon,
  DropdownArrowIcon,
  PersonalAccountIcon,
  PersonalCabinetIcon,
  SignOutIcon,
  SwapIcon
} from '@/components/ui/Icons/Icons';
import { Overlay } from '@/components/ui/Overlay/Overlay.styles';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { PROFILE_MODES, ProfileMode, ROUTES } from '@/constants/common';
import { useSignOut } from '@/hooks/useSignOut';

import * as S from './ProfileMenu.styles';

interface ProfileMenuItemProps {
  icon: ReactNode;
  label: string;
  dataCy: string;
  onClick: () => void;
  isDanger?: boolean;
}

const ProfileMenuItem: FC<ProfileMenuItemProps> = ({
  icon,
  label,
  dataCy,
  onClick,
  isDanger
}) => (
  <S.DropdownItem
    type="button"
    data-cy={dataCy}
    onClick={onClick}
    $isDanger={isDanger}
  >
    <S.IconBox aria-hidden>{icon}</S.IconBox>
    <Typography variant="body2">{label}</Typography>
  </S.DropdownItem>
);

interface ProfileMenuProps {
  name?: string | null;
  email?: string;
  profileMode: ProfileMode;
  image: string | null;
  onSwitchProfileMode: () => void;
  canSwitchProfileMode: boolean;
}

export const ProfileMenu: FC<ProfileMenuProps> = ({
  email,
  name,
  profileMode,
  image,
  onSwitchProfileMode,
  canSwitchProfileMode
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();
  const { push } = useRouter();

  const toggleDropdown = () => setIsOpen(prev => !prev);

  const handleSignOut = useSignOut();

  const profileRoute =
    profileMode === PROFILE_MODES.COMPANY
      ? ROUTES.COMPANY_PROFILE
      : ROUTES.PROFILE;

  const accountRoute = `${profileRoute}?section=${ProfileSectionEnum.Account}`;
  const applicationsRoute = `${profileRoute}?section=${ProfileSectionEnum.Applications}`;

  const isCompany = profileMode === PROFILE_MODES.COMPANY;

  const navigateTo = (route: string) => {
    setIsOpen(false);

    if (`${window.location.pathname}${window.location.search}` === route) {
      window.location.reload();
    } else {
      push(route);
    }
  };

  const handleGoToProfile = () => navigateTo(profileRoute);

  const handleGoToAccount = () => navigateTo(accountRoute);

  const handleGoToApplications = () => navigateTo(applicationsRoute);

  const handleSwitchProfileMode = () => {
    setIsOpen(false);
    onSwitchProfileMode();
  };

  return (
    <S.Wrapper data-cy="profile-menu">
      {isOpen && <Overlay $zIndex={5} onClick={() => setIsOpen(false)} />}

      <S.ToggleButton $isOpen={isOpen} onClick={toggleDropdown}>
        <Avatar size={34} name={name} image={image} />
        <S.Arrow $isOpen={isOpen}>
          <DropdownArrowIcon />
        </S.Arrow>
      </S.ToggleButton>

      <S.DropdownMenu role="listbox" $isOpen={isOpen}>
        <S.DropdownHeader
          type="button"
          data-cy="profile-menu-header-button"
          onClick={handleGoToProfile}
        >
          <Avatar size={34} name={name} image={image} />
          <S.EmailText>{email}</S.EmailText>
        </S.DropdownHeader>

        <ProfileMenuItem
          icon={<PersonalCabinetIcon />}
          label={t('ProfileMenu.profile_button_label')}
          dataCy="profile-menu-profile-button"
          onClick={handleGoToProfile}
        />

        <ProfileMenuItem
          icon={<PersonalAccountIcon />}
          label={t('ProfileMenu.personal_account_button_label')}
          dataCy="profile-menu-personal-account-button"
          onClick={handleGoToAccount}
        />

        {isCompany && (
          <ProfileMenuItem
            icon={<ApplicationsIcon />}
            label={t('ProfileMenu.applications_button_label')}
            dataCy="profile-menu-applications-button"
            onClick={handleGoToApplications}
          />
        )}

        <S.DividerWrapper>
          <S.Divider />
        </S.DividerWrapper>

        {canSwitchProfileMode && (
          <ProfileMenuItem
            icon={<SwapIcon />}
            label={
              profileMode === PROFILE_MODES.COMPANY
                ? t('ProfileMenu.switch_on_candidate_profile_label')
                : t('ProfileMenu.switch_on_company_profile_label')
            }
            dataCy="profile-menu-switch-profile-mode-button"
            onClick={handleSwitchProfileMode}
          />
        )}

        <ProfileMenuItem
          icon={<SignOutIcon />}
          label={t('ProfileMenu.signout_button_label')}
          dataCy="profile-menu-exit-button"
          onClick={handleSignOut}
          isDanger
        />
      </S.DropdownMenu>
    </S.Wrapper>
  );
};
